/**
 * Created by Ning Jiang on 2021-07-01.
 */

import {Layers, TombstoneRipLayers} from "./TombstoneRipLayers";
import {TombstoneRip} from "../TombstoneRip";
import {TombstoneRipGameAssets} from "../TombstoneRipGameAssets";
import {MathHelper} from "../../core/utils/MathHelper";
import {gsap} from "gsap";
import {ShootingController} from "./ShootingController";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {SuperMatchParticles} from "./SuperMatchParticles";
import {TombstoneRipSound} from "../sound/TombstoneRipSound";

export class SuperFsPop extends PIXI.Container {

    private _shuffleDuration:number = 1;
    private _shotgunDuration:number = 1;

    private _bottomSmoke:TimelineSprite;
    private _bottomFire:TimelineSprite;
    private _boardContainer:PIXI.Container;
    private _bg:PIXI.Sprite;
    private _symbol:PIXI.Sprite;
    private _glow:PIXI.Sprite;
    private _holesContainer:PIXI.Container;
    private _multiplier:PIXI.BitmapText;
    private _knifeContainer:PIXI.Container;
    private _topFire:TimelineSprite;
    private _shootSmokeContainer:PIXI.Container;

    private _timeline:TimelineLite | null;
    private _shotRange:PIXI.Point;
    private _skippable:boolean;
    private _moveTl:TimelineLite | null;

    private _shuffleGlowPlaying:boolean = false;
    private _shuffleGlowTl:TimelineLite | null;

    private _matchParticles:SuperMatchParticles;

    constructor() {
        super();

        this.visible = false;
        TombstoneRipLayers.getLayer(Layers.POP)?.addChild(this);
        this._matchParticles = new SuperMatchParticles();
        this._matchParticles.position.set(360, 360);
        TombstoneRipLayers.getLayer(Layers.POP)?.addChild(this._matchParticles);
        this.init();
    }

    private init():void {
        this._shotRange = new PIXI.Point(134 * 0.4, 184 * 0.4);

        this._bottomSmoke = new TimelineSprite(TombstoneRip.assetLoader.getTextures("nearWin/s4smoke/NWsmoke_"));
        this._bottomSmoke.scale.set(1.96, 1.41);
        this._bottomSmoke.anchor.set(0.5);
        this._bottomSmoke.position.set(-10, -64);
        this._bottomSmoke.hide();
        this.addChild(this._bottomSmoke);

        this._bottomFire = new TimelineSprite(TombstoneRip.assetLoader.getTextures("scatter/burning/burning_"));
        this._bottomFire.blendMode = PIXI.BLEND_MODES.ADD;
        this._bottomFire.scale.set(1.37, 1.17);
        this._bottomFire.anchor.set(0.5);
        this._bottomFire.position.set(-6, -94);
        this._bottomFire.hide();
        this.addChild(this._bottomFire);

        this._boardContainer = new PIXI.Container();
        this.addChild(this._boardContainer);

        this._bg = new PIXI.Sprite(TombstoneRip.assetLoader.getTextures("superfs/superPop")[0]);
        this._bg.anchor.set(0.5);
        this._bg.y = -10;
        this._boardContainer.addChild(this._bg);

        this._symbol = new PIXI.Sprite();
        this._symbol.anchor.set(0.5);
        this._symbol.scale.set(0.85);
        this._boardContainer.addChild(this._symbol);

        this._glow = new PIXI.Sprite(TombstoneRip.assetLoader.getTextures("popGlow")[0]);
        this._glow.blendMode = PIXI.BLEND_MODES.ADD;
        this._glow.anchor.set(0.5);
        this._glow.scale.set(1.6, 2.8);
        this._glow.y = -10;
        this._glow.visible = false;
        this._boardContainer.addChild(this._glow);

        this._holesContainer = new PIXI.Container();
        this._boardContainer.addChild(this._holesContainer);

        this._multiplier = new PIXI.BitmapText("", {
            fontName : TombstoneRipGameAssets.BITMAP_FONT_SUPERFS_GLOW_MUL,
            fontSize : 20,
            tint : 0xffffff
        });
        (<PIXI.Point>this._multiplier.anchor).set(1, 2);
        this._multiplier.position.set(this._shotRange.x - 5, this._shotRange.y - 10);
        this._multiplier.letterSpacing = -14;
        this._boardContainer.addChild(this._multiplier);

        this._knifeContainer = new PIXI.Container();
        this._boardContainer.addChild(this._knifeContainer);

        this._topFire = new TimelineSprite(TombstoneRip.assetLoader.getTextures("scatter/burning/burning_"));
        this._topFire.blendMode = PIXI.BLEND_MODES.ADD;
        this._topFire.scale.set(1.32, 1.5);
        this._topFire.anchor.set(0.5);
        this._topFire.position.set(0, -96);
        this._topFire.hide();
        this._boardContainer.addChild(this._topFire);

        this._shootSmokeContainer = new PIXI.Container();
        this.addChild(this._shootSmokeContainer);
    }

    public async show(animate:boolean, useMultCountUp:boolean, playMatchWhenReveal:boolean):Promise<void> {
        if(this._moveTl) {
            if(this._moveTl.isActive()) {
                this._moveTl.progress(1);
            }
            this._moveTl = null;
        }

        this._skippable = animate;
        return new Promise(resolve => {
            const tl = gsap.timeline();
            const data = TombstoneRip.model.parsedGameData.superFSInfo;
            tl.add(() => {
                this._multiplier.visible = false;
                this.position.set(360, 360);
                this.visible = true;
                this._bottomSmoke.show();
                this._bottomSmoke.playLoop();
                this._bottomFire.show();
                this._bottomFire.playLoop();
                if(animate) {
                    this.playShuffleGlow();
                    this.scale.set(1.5, 1.5);
                } else {
                    this.scale.set(1, 1);
                }
            });
            if(animate) {
                tl.add([
                    gsap.to(this.scale, {duration : 0.4, x : 1, y : 1}),
                    gsap.to(this, {
                        duration : TombstoneRip.apiPlugin.settings.get(APISetting.FAST_SPIN) ? (this._shuffleDuration * 0.8) : this._shuffleDuration,
                        onUpdate : () => {
                            const random = MathHelper.randomNumberInRange(1, 5);
                            this._symbol.texture = TombstoneRip.assetLoader.getTextures(`M${random}/M${random}_C1`)[0];
                        }
                    }),
                    () => {TombstoneRipSound.soundPlayer.playTrack({name : TombstoneRipSound.POP_UP_SHUFFLE})}
                ]);
            }
            tl.add(() => {
                this._symbol.texture = TombstoneRip.assetLoader.getTextures(`${data.outlawMultiplier}/${data.outlawMultiplier}_C1`)[0];
                TombstoneRipSound.soundPlayer.playTrack({name : TombstoneRipSound.POP_UP_SYMBOL_LAND});
                if(animate) {
                    this.stopShuffleGlow();
                    TombstoneRipSound.fadeAndStopSound({
                        name : TombstoneRipSound.POP_UP_SHUFFLE,
                        to : 0,
                        duration : 100
                    });
                    ShootingController.shotAt(ShootingController.GUN_SHOTGUN, true,
                        this._shootSmokeContainer,
                        new PIXI.Point(
                            MathHelper.randomNumberInRange(-this._shotRange.x, this._shotRange.x),
                            MathHelper.randomNumberInRange(-this._shotRange.y, this._shotRange.y)
                        ), true, this._holesContainer);
                    this.shakeMe();
                }
                TombstoneRip.reels.forEach(reel => {
                    reel.superMultiplierM = data.outlawMultiplier;
                });
                if(playMatchWhenReveal) {
                    if(data.outlawWild === data.outlawMultiplier) {
                        this.playMatch();
                    }
                }
            });
            tl.add(() => this.playShotMultiplier(0, data.outlawMultiplierValue, useMultCountUp, 10 + Math.floor(Math.random() * 5), resolve));
            this._timeline = tl;
        });
    }

    private playShuffleGlow():void {
        this._shuffleGlowPlaying = true;
        this._glow.visible = true;
        this.loopShuffleGlow();
    }

    private loopShuffleGlow():void {
        if(!this._shuffleGlowPlaying) {
            return;
        }

        const tl = gsap.timeline();
        tl.fromTo(this._glow, {alpha : 0}, {alpha : 1, duration : 0.066})
            .to(this._glow, {alpha : 0, duraiton : 0.167});
        tl.add(() => this.loopShuffleGlow());
        this._shuffleGlowTl = tl;
    }

    private stopShuffleGlow():void {
        this._shuffleGlowPlaying = false;
        if(this._shuffleGlowTl && this._shuffleGlowTl.isActive()) {
            this._shuffleGlowTl.pause();
            this._shuffleGlowTl.kill();
        }
        this._shuffleGlowTl = null;
        this._glow.visible = false;
    }

    private playShotMultiplier(currentDisplay:number, multLeft:number, useMultCountUp:boolean, countUpStep:number, resolve:() => void):void {
        if(multLeft === 0 || !useMultCountUp) {
            this._skippable = false;
            this._multiplier.text = `x${TombstoneRip.model.parsedGameData.superFSInfo.outlawMultiplierValue}`;
            this._multiplier.visible = true;
            gsap.fromTo(this._multiplier.scale, {x : 1.1, y : 1.1}, {duration : 0.2, x : 1, y : 1});
            resolve();
            return;
        }

        if(countUpStep > 100) {
            countUpStep = 80 + Math.floor(Math.random() * 20);
        }

        this._skippable = true;
        const tl = gsap.timeline();
        tl.add(gsap.to(this, {duration : TombstoneRip.apiPlugin.settings.get(APISetting.FAST_SPIN) ? (this._shotgunDuration * 0.8) : this._shotgunDuration})); // pause 1 sec on each big shot
        if(multLeft < countUpStep) {
            //play small
            while(multLeft > 0) {
                const step = Math.max(1, Math.floor(Math.random() * multLeft));
                tl.add(() => {
                    this._multiplier.text = `x${currentDisplay + step}`;
                    currentDisplay += step;
                    this._multiplier.visible = true;
                    ShootingController.shotAt(ShootingController.GUN_PISTOL, false,
                        this._shootSmokeContainer,
                        new PIXI.Point(
                            MathHelper.randomNumberInRange(-this._shotRange.x, this._shotRange.x),
                            MathHelper.randomNumberInRange(-this._shotRange.y, this._shotRange.y)
                        ),
                        true, this._holesContainer);
                    this.shakeMe();
                }, `+=0.1`);
                multLeft -= step;
            }
        } else {
            // play big
            tl.add(() => {
                this._multiplier.text = `x${currentDisplay + countUpStep}`;
                this._multiplier.visible = true;
                ShootingController.shotAt(ShootingController.GUN_SHOTGUN, false,
                    this._shootSmokeContainer,
                    new PIXI.Point(
                        MathHelper.randomNumberInRange(-this._shotRange.x, this._shotRange.x),
                        MathHelper.randomNumberInRange(-this._shotRange.y, this._shotRange.y)
                    ),
                    true, this._holesContainer);
                this.shakeMe();
            });
            tl.add(() => {
                currentDisplay += countUpStep;
                multLeft -= countUpStep;
            });
        }
        tl.add(() => this.playShotMultiplier(currentDisplay, multLeft, useMultCountUp, Math.floor(countUpStep * (1.1 + Math.random() * 0.2)), resolve));
        this._timeline = tl;
    }

    public skip():boolean {
        if(this._timeline && this._timeline.isActive()) {
            this._timeline.progress(1);
        }
        return this._skippable;
    }

    public moveToSide():void {
        const tl = gsap.timeline();
        tl.add([
            () => {
                if(!this._topFire || !this._topFire.visible) {
                    this._bottomSmoke.hide();
                    this._bottomSmoke.stopLoop();
                    this._bottomFire.hide();
                    this._bottomFire.stopLoop();
                }
            },
            gsap.to(this.position, {x : 76, y : 620, duration : 0.2, ease : "power4.in"}),
            gsap.to(this.scale, {x : 0.6, y : 0.6, duration : 0.2, ease : "power4.in"}),
            () => TombstoneRipSound.soundPlayer.playTrack({name : TombstoneRipSound.POP_UP_MOVE})
        ]);
        this._moveTl = tl;
    }

    public reset():void {
        if(this.visible) {
            this.visible = false;
            this._skippable = false;
            this._timeline = null;
            this._holesContainer.children.length = 0;
            this._shootSmokeContainer.children.length = 0;
            this._bottomSmoke.hide();
            this._bottomSmoke.stopLoop();
            this._bottomFire.hide();
            this._bottomFire.stopLoop();
            this.stopFire();
            this._matchParticles.stop();
            TombstoneRipSound.fadeAndStopSound({
                name : TombstoneRipSound.REELS_ON_FIRE,
                to : 0,
                duration : 200
            });
        }
    }

    public playMatch():TimelineLite {
        const matchTl = gsap.timeline();
        matchTl.add(() => {
            TombstoneRipSound.soundPlayer.playTrack({name : TombstoneRipSound.MATCH_EXPLOSION});
            TombstoneRip.reel5Frame.playFire();
            TombstoneRip.background.playSuperFsMatch();
            this._matchParticles.play();
            this.playFire();
            const symName = TombstoneRip.model.parsedGameData.superFSInfo.outlawMultiplier
            this._symbol.texture = TombstoneRip.assetLoader.getTextures(`${symName}/${symName}_C3`)[0];
        });
        matchTl.add(() => {
            // this._yehaa.visible = true;
            // this._yehaa.alpha = 1;
        }, 0.3);

        return matchTl;
    }

    private playFire():void {
        this._topFire.show();
        this._topFire.playLoop();
        this.playShuffleGlow();
    }

    private stopFire():void {
        this._bottomFire.hide();
        this._bottomFire.stopLoop();
        this._topFire.hide();
        this._topFire.stopLoop();
        this.stopShuffleGlow();
    }

    private shakeMe():void {
        const tl = gsap.timeline();
        tl.fromTo(this._boardContainer, {x : 0, y : 0}, {
            x : MathHelper.randomNumberInRange(-5, 5),
            y : MathHelper.randomNumberInRange(0, 5),
            duration : 0.1
        })
            .to(this._boardContainer, {x : 0, y : 0, duration : 0.2});
    }
}